import PropTypes from 'prop-types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button, Divider } from '@/components'
import CheckCircleIcon from '@/assets/icons/check-circle.svg'
import { plaidPrepare, plaidOpen, isPlaidReady } from '@/lib/plaid-link-bus'

const PlaidTransactionPickerConfirmationModal = ({
  isOpen,
  onClose,
  onCloseMainModal,
  type, // 'skip' or 'submit'
  onConfirm,
}) => {
  const isSkip = type === 'skip'

  const handleLinkAnotherAccount = async () => {
    // Close both the confirmation modal and the main "Identify" modal
    onClose()
    if (onCloseMainModal) {
      onCloseMainModal()
    }
    // Trigger the Plaid Link flow after modals close
    // Add a small delay to ensure modals are fully closed before opening Plaid
    setTimeout(async () => {
      // Only prepare if not already ready to avoid re-initialization delay
      if (!isPlaidReady()) {
        plaidPrepare()
      }
      await plaidOpen()
    }, 50)
  }

  const handleDialogClose = (open) => {
    // For submit type, closing (X or outside click) should do the same as clicking Close button
    if (open === false && !isSkip) {
      onConfirm()
    } else if (open === false) {
      // For skip type, just close normally
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogClose}>
      <DialogContent
        onClose={!isSkip ? onConfirm : onClose}
        className="max-w-[92%] rounded-lg md:max-w-[480px] p-0"
        closable={true}
      >
        <DialogHeader className="p-5 pb-2 mb-4">
          <div className="flex flex-row justify-start items-start">
            <div className="flex items-center justify-center w-12 h-12 rounded-[10px] flex-shrink-0 bg-gradient-to-r from-[--brand-850] to-[--brand-900]">
              {isSkip ? (
                <div className="brightness-0 invert flex items-center justify-center" style={{ width: '24px', height: '24px' }}>
                  <svg width="24" height="24" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.99967 5.3335V8.00016M7.99967 10.6668H8.00634M14.6663 8.00016C14.6663 11.6821 11.6816 14.6668 7.99967 14.6668C4.31778 14.6668 1.33301 11.6821 1.33301 8.00016C1.33301 4.31826 4.31778 1.3335 7.99967 1.3335C11.6816 1.3335 14.6663 4.31826 14.6663 8.00016Z"
                      stroke="white"
                      strokeWidth="1.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              ) : (
                <img src={CheckCircleIcon} alt="Check circle" className="w-6 h-6 brightness-0 invert" />
              )}
            </div>
            <div className="ml-5 flex flex-col justify-center items-start">
              <DialogTitle className="leading-7 text-neutral-900 text-lg font-semibold mb-1 text-left">
                {isSkip 
                  ? 'Are you sure you want to skip?'
                  : 'Your selection was submitted!'
                }
              </DialogTitle>
              <DialogDescription className="text-neutral-700 text-sm font-normal text-start">
                {isSkip
                  ? 'Skipping now means we will be unable to monitor your account for loan or lease payments. To remain eligible, you must email loan or lease documentation to help@fasten.cc'
                  : 'Our team will review your selection. Once you have reached the minimum spend threshold ($500) in a given month, you will be eligible to be rewarded for the next instance of your loan or lease repayment.'
                }
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Divider />

        <DialogFooter className="w-full px-5 flex-col gap-3">
          {isSkip ? (
            <>
              <div className="w-full flex justify-end">
                <button
                  onClick={handleLinkAnotherAccount}
                  className="text-[--brand-500] font-semibold text-sm hover:opacity-80 cursor-pointer"
                >
                  Link Another Account
                </button>
              </div>
              <div className="w-full flex gap-4">
                <Button
                  variant="outlineSecondary"
                  className="flex-1 hover:bg-dGray-100"
                  onClick={onClose}
                >
                  Back
                </Button>
                <Button
                  className="flex-1 bg-[--error-500] hover:bg-[--error-600] focus:bg-[--error-500] text-white"
                  onClick={onConfirm}
                >
                  Skip
                </Button>
              </div>
            </>
          ) : (
            <Button
              className="w-full bg-brand-500 hover:bg-brand-600"
              onClick={onConfirm}
            >
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

PlaidTransactionPickerConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseMainModal: PropTypes.func,
  type: PropTypes.oneOf(['skip', 'submit']).isRequired,
  onConfirm: PropTypes.func.isRequired,
}

export default PlaidTransactionPickerConfirmationModal
